import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { ArrowLeft, Upload, X } from 'lucide-react'
import { categoryService } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'

const CategoryForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const isEditing = Boolean(id)

  const [form, setForm] = useState({
    name: '',
    description: '',
    parent_id: '',
    sort_order: '',
    image: null
  })

  const [imagePreview, setImagePreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Fetch category data if editing
  const { data: category, isLoading: categoryLoading } = useQuery({
    queryKey: ['category', id],
    queryFn: () => categoryService.getCategory(id),
    enabled: isEditing,
  })

  // Update form when category data is loaded
  useEffect(() => {
    if (category && isEditing) {
      setForm({
        name: category.name || '',
        description: category.description || '',
        parent_id: category.parent_id || '',
        sort_order: category.sort_order || '',
        image: null
      })
      
      if (category.image) {
        setImagePreview(`/uploads/categories/${category.image}`)
      }
    }
  }, [category, isEditing])

  const { data: categories } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => categoryService.getCategories(true)
  })

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setForm({ ...form, image: file })
      const reader = new FileReader()
      reader.onload = (e) => setImagePreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const removeImage = () => {
    setForm({ ...form, image: null })
    setImagePreview(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const formData = new FormData()
      formData.append('name', form.name)
      formData.append('description', form.description)
      
      if (form.parent_id && form.parent_id !== '') {
        formData.append('parent_id', form.parent_id)
      }
      
      if (form.sort_order && form.sort_order !== '') {
        formData.append('sort_order', form.sort_order)
      }
      
      if (form.image) {
        formData.append('image', form.image)
      }

      if (isEditing) {
        await categoryService.updateCategory(id, formData)
        toast.success('Category updated successfully')
      } else {
        await categoryService.createCategory(formData)
        toast.success('Category created successfully')
      }

      queryClient.invalidateQueries('admin-categories')
      navigate('/admin?tab=categories')
    } catch (error) {
      console.error('Error saving category:', error)
      setError(error.message || 'Failed to save category')
    } finally {
      setLoading(false)
    }
  }

  if (categoryLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  // Filter out the current category from parent options when editing
  const parentOptions = categories?.flat_categories?.filter(cat => 
    !isEditing || cat.id !== parseInt(id)
  ) || []

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin?tab=categories')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          العودة إلى الفئات
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'تعديل الفئة' : 'إنشاء فئة جديدة'}
        </h1>
      </div>

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              اسم الفئة *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              الوصف
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              الفئة الأساسية
            </label>
            <select
              value={form.parent_id}
              onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">لا يوجد (فئة رئيسية)</option>
              {parentOptions.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ترتيب الفرز
            </label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="أدخل ترتيب الفرز (اختياري، سيتم تعيينه تلقائياً إذا ترك فارغاً)"
              min="0"
            />
            <p className="text-sm text-gray-500 mt-1">
              الأرقام الأقل تظهر أولاً. اتركه فارغاً لتعيين الرقم التالي تلقائياً.
            </p>
          </div>

          {/* Category Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              صورة الفئة
            </label>
            {imagePreview ? (
              <div className="relative inline-block">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-40 h-40 object-cover rounded-lg border"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="mx-auto h-8 w-8 text-gray-400" />
                <div className="mt-2">
                  <label htmlFor="image" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      رفع الصورة
                    </span>
                    <input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/admin?tab=categories')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-secondary text-white rounded-md hover:bg-secondary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'جاري الحفظ...' : (isEditing ? 'تحديث الفئة' : 'إنشاء الفئة')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CategoryForm
